import React, { useEffect, useState } from "react";
import CanvasImageLayer from "./CanvasImageLayer";
import { FixedSizeGrid as Grid } from "react-window";
import Privacy from "./Privacy";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Success from "./Success";
import Terms from "./Terms";

function getGridSize() {
  const width = window.innerWidth;
  if (width < 500) return { grid: 360, tile: 3.6 };
  if (width < 768) return { grid: 500, tile: 5 };
  return { grid: 1000, tile: 10 };
}

const columnCount = 100;
const rowCount = 100;

function TileGrid() {
  const [unlockedTiles, setUnlockedTiles] = useState(new Set());
  const [reservedEyeTileIds, setReservedEyeTileIds] = useState(new Set());
  const [macroTilesRevealed, setMacroTilesRevealed] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [stage2Triggered, setStage2Triggered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justRevealed, setJustRevealed] = useState(null);

  const { grid: visualGridSize, tile: tileSize } = getGridSize();

  const fetchUnlockedTiles = async () => {
    try {
      const res = await fetch("https://findmyface-backend.onrender.com/tiles/unlocked?user_id=1");
      const data = await res.json();
      const filtered = data.unlocked_tiles.filter((id) => !reservedEyeTileIds.has(id));

      const newTileId = filtered.find((id) => !unlockedTiles.has(id));
      if (newTileId) {
        setJustRevealed(newTileId);
        setTimeout(() => setJustRevealed(null), 1000);
      }

      setUnlockedTiles(new Set(filtered));
      document.title = `${filtered.length} tiles unlocked — Find My Face`;

      const revealedMacro = new Set();
      for (let row = 0; row < 100; row++) {
        for (let col = 0; col < 100; col++) {
          let anyUnlocked = false;
          for (let dy = 0; dy < 10 && !anyUnlocked; dy++) {
            for (let dx = 0; dx < 10 && !anyUnlocked; dx++) {
              const id = (row * 10 + dy) * 1000 + (col * 10 + dx);
              if (filtered.includes(id)) anyUnlocked = true;
            }
          }
          if (anyUnlocked) revealedMacro.add(row * 100 + col);
        }
      }
      setMacroTilesRevealed(revealedMacro.size);
      if (revealedMacro.size >= 3000 && !stage2Triggered) {
        setShowModal(true);
        setStage2Triggered(true);
      }
    } catch (err) {
      console.error("❌ Failed to fetch tiles:", err);
    }
  };

  useEffect(() => {
    fetch("/reservedEyeTiles.json")
      .then((res) => res.json())
      .then((data) => setReservedEyeTileIds(new Set(data)))
      .catch((err) => console.error("Failed to load reserved eye tiles:", err));
  }, []);

  useEffect(() => {
    fetchUnlockedTiles();
    const interval = setInterval(fetchUnlockedTiles, 10000);
    return () => clearInterval(interval);
  }, [reservedEyeTileIds, stage2Triggered]);

  const handlePurchase = async () => {
    if (!window.Stripe) {
      alert("Stripe.js failed to load.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("https://findmyface-backend.onrender.com/create-checkout-session?user_id=1", {
        method: "POST",
      });
      const data = await res.json();

      if (data.id) {
        const stripe = window.Stripe("pk_live_51RQ73EG8DDYxRIZ0QdEGkHYedOZHSdSUIlTB5j4oYb0BCCY87ALHzf0ZjaUXD9Mw6jg0PQTwtTvlGtkkYaOsCior0090Jn2yaK");
        await stripe.redirectToCheckout({ sessionId: data.id });
      } else {
        alert("Failed to create session.");
        console.error(data.error);
      }
    } catch (err) {
      alert("Error contacting server.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  function Tile({ columnIndex, rowIndex, style }) {
    const trueX = columnIndex * 10;
    const trueY = rowIndex * 10;
    const tileIds = [];

    for (let dy = 0; dy < 10; dy++) {
      for (let dx = 0; dx < 10; dx++) {
        tileIds.push((trueY + dy) * 1000 + (trueX + dx));
      }
    }

    const isUnlocked = tileIds.some((id) => unlockedTiles.has(id));
    const isReserved = tileIds.some((id) => reservedEyeTileIds.has(id));
    const isJustRevealed = tileIds.includes(justRevealed);

    return (
      <div
        className={isJustRevealed ? "reveal-anim" : ""}
        style={{
          ...style,
          backgroundColor: isUnlocked ? "#ff69b4" : "#000",
          border: isUnlocked ? "1px solid #ff1493" : "1px solid #111",
          boxSizing: "border-box",
          cursor: isUnlocked || isReserved ? "default" : "pointer",
          transition: "all 0.6s ease-in-out",
          transform: isUnlocked ? "scale(1)" : "scale(0.95)",
          opacity: isUnlocked ? 1 : 0.8,
          animation: isReserved && macroTilesRevealed >= 3000 ? "pulse 1.8s infinite" : "none",
        }}
        onClick={() => {
          if (!isUnlocked && !isReserved) {
            alert(`Tile ${trueY * 1000 + trueX} is locked.`);
          }
        }}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#000",
        fontFamily: "'IBM Plex Sans', sans-serif",
        padding: "40px 20px",
        position: "relative",
      }}
    >
      <h2
        style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: "clamp(28px, 6vw, 48px)",
          color: "#ff00cc",
          marginBottom: "30px",
          textAlign: "center",
          maxWidth: "90vw",
          wordWrap: "break-word",
        }}
      >
        🧩 Find My Face
      </h2>

      <div
        style={{
          position: "relative",
          width: visualGridSize,
          height: visualGridSize,
          margin: "0 auto",
          border: "4px solid #ff00aa",
          boxShadow: "0 0 20px #ff00aa",
        }}
      >
        <img
          src="/findmyface_square_1000x1000.jpg"
          alt="ghost"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: macroTilesRevealed >= 2000 ? Math.min((macroTilesRevealed - 2000) / 1000, 1) * 0.4 : 0,
            filter: "grayscale(100%) blur(2px)",
            transition: "opacity 1s ease-in-out",
            zIndex: 1,
          }}
        />
        <CanvasImageLayer
          unlockedTiles={[...unlockedTiles]}
          imageUrl="/findmyface_square_1000x1000.jpg"
          tileSize={1}
          columns={1000}
          rows={1000}
        />
        <Grid
          columnCount={columnCount}
          rowCount={rowCount}
          columnWidth={tileSize}
          rowHeight={tileSize}
          height={visualGridSize}
          width={visualGridSize}
          overscanRowCount={5}
          overscanColumnCount={5}
          style={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}
        >
          {Tile}
        </Grid>
      </div>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.94)",
            color: "#fff",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "clamp(20px, 6vw, 28px)",
            fontWeight: "bold",
            textAlign: "center",
            padding: "40px",
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}
          onClick={() => setShowModal(false)}
        >
          You shouldn’t have gotten this far.
        </div>
      )}

      <button
        onClick={handlePurchase}
        disabled={loading}
        style={{
          marginTop: "20px",
          padding: "14px 28px",
          fontSize: "16px",
          backgroundColor: "#ff00aa",
          color: "#fff",
          border: "none",
          borderRadius: "30px",
          cursor: "pointer",
          boxShadow: "0 0 10px rgba(255, 0, 170, 0.5)",
        }}
      >
        {loading ? "Redirecting..." : "Buy Next Tile for $1"}
      </button>
      <p
        style={{
          color: "#777",
          fontSize: "12px",
          marginTop: "12px",
          maxWidth: "400px",
          textAlign: "center",
          lineHeight: 1.4,
        }}
      >
        By purchasing, you agree to the terms of participation. This is a digital art experience. No physical product is delivered. All purchases are final and non-refundable.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<TileGrid />} />
        <Route path="/success" element={<Success />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </Router>
  );
}
